import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Stack,
  Typography,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Alert,
} from "@mui/material";
import HeroEditor from "../components/hero/HeroEditor";
import FeaturedProductsPicker from "../components/hero/FeaturedProductsPicker";
import HeroPreview from "../components/hero/HeroPreview"; // We'll create this
import {
  adminCreateHero,
  adminDeleteHero,
  adminDuplicateHero,

  adminGetHeroProducts,
  adminListHeros,
  adminSetHeroProducts,
  adminUpdateHero,
  type HeroSettings,
  type HeroProductInput,
  type HeroProductRow,
} from "../api/adminHeroApi";

const emptyHero: Partial<HeroSettings> = {
  headline: "",
  subheadline: "",
  hero_layout_type: "hero",
  background_type: "image",
  overlay_opacity: 0.45,
  content_align: "left",
  content_position: "left",
  media_position: "right",
  banner_height: "standard",
  carousel_enabled: false,
  primary_cta_text: null,
  primary_cta_link: null,
  secondary_cta_text: null,
  secondary_cta_link: null,
  promo_enabled: false,
  promo_text: "",
  loyalty_highlight: false,
  is_active: false,
  start_at: null,
  end_at: null,
  background_url: null,
  background_url_mobile: null,
  gradient_css: null,
};

const PLACEMENTS: { value: string; label: string; wholesale?: boolean }[] = [
  { value: "all", label: "All" },
  { value: "home", label: "Homepage" },
  { value: "shop", label: "Shop" },
  { value: "checkout", label: "Checkout" },
  { value: "global", label: "Global" },
  { value: "wholesale_home", label: "Wholesale Home", wholesale: true },
];

const placementLabel = (p?: string) =>
  PLACEMENTS.find((x) => x.value === (p || "home"))?.label || p || "Homepage";

const isWholesalePlacement = (p?: string) => p === "wholesale_home";

export default function HeroSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [heroes, setHeroes] = useState<HeroSettings[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [placementFilter, setPlacementFilter] = useState<string>("all");

  const [draft, setDraft] = useState<Partial<HeroSettings>>(emptyHero);

  const [heroProducts, setHeroProducts] = useState<HeroProductInput[]>([]);
  const [heroProductsResolved, setHeroProductsResolved] = useState<HeroProductRow[]>([]);

  const selectedHero = useMemo(
    () => heroes.find((h) => h.id === selectedId) || null,
    [heroes, selectedId]
  );

  const filteredHeroes = useMemo(
    () => (placementFilter === "all" ? heroes : heroes.filter((h) => (h.placement || "home") === placementFilter)),
    [heroes, placementFilter]
  );

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const list = await adminListHeros();
      setHeroes(list);
      if (list.length && !selectedId) setSelectedId(list[0].id);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || "Failed to load heroes");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When selecting a hero, load draft + products
  useEffect(() => {
    async function loadHeroProducts(heroId: string) {
      setError(null);
      try {
        const rows = await adminGetHeroProducts(heroId);
        setHeroProductsResolved(rows);
        setHeroProducts(
          rows
            .slice()
            .sort((a, b) => a.sort_order - b.sort_order)
            .map((r, idx) => ({
              product_id: r.product_id,
              sort_order: idx,
              badge_text: r.badge_text,
              use_product_image: r.use_product_image,
            }))
        );
      } catch (e: any) {
        setError(e?.response?.data?.error || e?.message || "Failed to load hero products");
        setHeroProductsResolved([]);
        setHeroProducts([]);
      }
    }

    if (selectedHero) {
      setDraft(selectedHero);
      loadHeroProducts(selectedHero.id);
    } else {
      setDraft(emptyHero);
      setHeroProductsResolved([]);
      setHeroProducts([]);
    }
  }, [selectedHero]);

  async function onCreate() {
    setSaving(true);
    setError(null);
    try {
      const created = await adminCreateHero({
        ...emptyHero,
        headline: "New Hero",
        subheadline: "",
        is_active: false,
        // Seed the new hero with the currently-filtered placement so it lands in
        // the section the admin is looking at (e.g. Wholesale Home).
        ...(placementFilter !== "all" ? { placement: placementFilter as HeroSettings["placement"] } : {}),
      });
      await refresh();
      setSelectedId(created.id);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || "Failed to create hero");
    } finally {
      setSaving(false);
    }
  }

  async function onSave() {
    if (!draft.headline?.trim()) {
      setError("Headline is required");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (!selectedHero?.id) {
        const created = await adminCreateHero(draft);
        setSelectedId(created.id);
      } else {
        await adminUpdateHero(selectedHero.id, draft);
        await adminSetHeroProducts(selectedHero.id, heroProducts.map((p, idx) => ({ ...p, sort_order: idx })));
      }

      await refresh();
      if (selectedHero?.id) setSelectedId(selectedHero.id);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || "Failed to save hero");
    } finally {
      setSaving(false);
    }
  }

    async function onDuplicate() {
    if (!selectedHero?.id) return;

    setSaving(true);
    setError(null);

    try {
      const duplicated = await adminDuplicateHero(selectedHero.id);
      await refresh();
      setSelectedId(duplicated.id);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || "Failed to duplicate hero");
    } finally {
      setSaving(false);
    }
  }

  async function onDelete() {
    if (!selectedHero?.id) return;
    if (!window.confirm("Delete this hero? This cannot be undone.")) return;

    setSaving(true);
    setError(null);
    try {
      await adminDeleteHero(selectedHero.id);
      setSelectedId(null);
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || "Failed to delete hero");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <Box p={3} display="flex" alignItems="center" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={800}>
            Hero Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage multiple active heroes + featured products.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={onCreate} disabled={saving}>
            New Hero
          </Button>
                    <Button variant="contained" onClick={onSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
          <Button variant="outlined" onClick={onDuplicate} disabled={!selectedHero || saving}>
            Duplicate
          </Button>
          <Button color="error" variant="outlined" onClick={onDelete} disabled={!selectedHero || saving}>
            Delete
          </Button>
        </Stack>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2}>
        {/* Left: list */}
        <Grid item xs={12} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography fontWeight={700}>Heroes</Typography>
                <Chip size="small" label={`${filteredHeroes.length}`} />
              </Stack>

              {/* Placement filter — separates wholesale vs retail heroes */}
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                {PLACEMENTS.map((p) => {
                  const count =
                    p.value === "all"
                      ? heroes.length
                      : heroes.filter((h) => (h.placement || "home") === p.value).length;
                  const active = placementFilter === p.value;
                  return (
                    <Chip
                      key={p.value}
                      size="small"
                      label={`${p.label} (${count})`}
                      onClick={() => setPlacementFilter(p.value)}
                      variant={active ? "filled" : "outlined"}
                      sx={
                        p.wholesale
                          ? {
                              color: active ? "#06251C" : "#1c7a5e",
                              bgcolor: active ? "#2FD3A5" : "transparent",
                              borderColor: "#2FD3A5",
                              fontWeight: 700,
                            }
                          : { fontWeight: active ? 700 : 400 }
                      }
                      color={p.wholesale ? undefined : active ? "primary" : "default"}
                    />
                  );
                })}
              </Stack>

              <Divider sx={{ mb: 1 }} />
              <List dense sx={{ maxHeight: 520, overflow: "auto" }}>
                {filteredHeroes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    No heroes for this placement yet. Click “New Hero”, then set its
                    placement to add one.
                  </Typography>
                ) : null}
                {filteredHeroes.map((h) => (
                  <ListItemButton
                    key={h.id}
                    selected={h.id === selectedId}
                    onClick={() => setSelectedId(h.id)}
                    sx={{ borderRadius: 2 }}
                  >
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography fontWeight={800}>{h.headline}</Typography>
                          <Chip
                            size="small"
                            label={placementLabel(h.placement)}
                            variant="outlined"
                            sx={
                              isWholesalePlacement(h.placement)
                                ? { color: "#1c7a5e", borderColor: "#2FD3A5", bgcolor: "rgba(47,211,165,0.10)", fontWeight: 700 }
                                : undefined
                            }
                          />
                          {h.is_active ? <Chip size="small" color="success" label="Active" /> : <Chip size="small" label="Inactive" />}
                          {h.promo_enabled ? <Chip size="small" color="primary" label="Promo" /> : null}
                        </Stack>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {h.subheadline || "—"}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Right: editor */}
        <Grid item xs={12} md={8}>
          <Stack spacing={2}>
            <HeroEditor value={draft} onChange={setDraft} />

            <FeaturedProductsPicker
              value={heroProducts}
              onChange={setHeroProducts}
              resolved={heroProductsResolved}
            />

            {/* Live Preview */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Live Preview
                </Typography>
                <HeroPreview
                  hero={draft}
                  products={heroProductsResolved}
                />
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}